import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import {
  LAUNCH_GATE,
  LAUNCH_TARGET,
  MACHINE_LOOP,
  STRATEGY_ENGINES,
  type NodeStatus,
  type StrategyNode,
} from '../data/strategyMap';

const STATUS_META: Record<NodeStatus, { label: string; tone: string }> = {
  idea: { label: 'Idea', tone: 'admin-status-neutral' },
  mocked: { label: 'Mocked', tone: 'admin-status-accent' },
  approved: { label: 'Approved', tone: 'admin-status-warning' },
  building: { label: 'Building', tone: 'admin-status-warning' },
  live: { label: 'Live', tone: 'admin-status-success' },
};

const STATUS_ORDER: NodeStatus[] = ['idea', 'mocked', 'approved', 'building', 'live'];

/** Title lookup so dependsOn chips can render human names. */
const NODE_TITLES: Record<string, string> = Object.fromEntries(
  STRATEGY_ENGINES.flatMap((e) => e.nodes.map((n) => [n.id, n.title])),
);

function daysUntil(iso: string): number {
  const target = new Date(`${iso}T00:00:00-05:00`).getTime();
  return Math.ceil((target - Date.now()) / 86400000);
}

function NodeCard({ node }: { node: StrategyNode }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[node.status];
  return (
    <div className="admin-card !p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.03]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] font-medium text-textPrimary">{node.title}</span>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-muted">{node.summary}</p>
        </div>
        <span className={`admin-status ${meta.tone} shrink-0`}>{meta.label}</span>
      </button>
      {open ? (
        <div className="border-t border-border/60 px-4 py-3.5">
          <ul className="space-y-2">
            {node.detail.map((d, i) => (
              <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-muted">
                <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-accent/70" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
          {node.dependsOn && node.dependsOn.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.08em] text-mutedDim">Needs</span>
              {node.dependsOn.map((id) => (
                <span key={id} className="admin-chip-id">
                  {NODE_TITLES[id] ?? id}
                </span>
              ))}
            </div>
          ) : null}
          {node.link ? (
            <Link to={node.link.to} className="admin-btn admin-btn-secondary admin-btn-sm mt-3">
              {node.link.label} →
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function StrategyScreen() {
  const countdown = daysUntil(LAUNCH_TARGET.dateISO);

  const statusCounts = useMemo(() => {
    const counts = new Map<NodeStatus, number>();
    STRATEGY_ENGINES.forEach((e) => e.nodes.forEach((n) => counts.set(n.status, (counts.get(n.status) ?? 0) + 1)));
    return counts;
  }, []);

  return (
    <div className="admin-page max-w-6xl mx-auto">
      <PageHeader
        title="Strategy"
        subtitle="The growth machine — three engines, one launch window. Tap a card for the why."
      />

      {/* Launch gate + countdown */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="admin-card lg:col-span-2 border-warning/25">
          <div className="admin-eyebrow mb-2 !text-[#e3b341]">{LAUNCH_GATE.title}</div>
          <p className="text-[12.5px] leading-relaxed text-muted">{LAUNCH_GATE.body}</p>
        </div>
        <div className="admin-card admin-hero-wash flex flex-col justify-center">
          <div className="admin-eyebrow mb-1">{LAUNCH_TARGET.label}</div>
          <div className="font-display text-[30px] font-semibold leading-tight tracking-[-0.02em] text-textPrimary tabular-nums">
            {countdown >= 0 ? `${countdown} days` : `${Math.abs(countdown)} days ago`}
          </div>
          <p className="mt-1 text-[11px] text-mutedDim">
            {new Date(`${LAUNCH_TARGET.dateISO}T12:00:00Z`).toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })}{' '}
            — the year’s best acquisition window for this audience.
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUS_ORDER.map((s) => (
          <span key={s} className={`admin-status ${STATUS_META[s].tone}`}>
            {STATUS_META[s].label}
            <span className="ml-1 tabular-nums opacity-70">{statusCounts.get(s) ?? 0}</span>
          </span>
        ))}
      </div>

      {/* The three engines */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {STRATEGY_ENGINES.map((engine) => (
          <div key={engine.id} className="min-w-0">
            <div className="mb-3 rounded-lg border border-border bg-surfaceElevated px-4 py-3">
              <div className="font-display text-[15px] font-semibold tracking-tight text-textPrimary">
                {engine.name}
              </div>
              <p className="mt-1 text-[11.5px] leading-relaxed text-mutedDim">{engine.tagline}</p>
            </div>
            <div className="space-y-2.5">
              {engine.nodes.map((n) => (
                <NodeCard key={n.id} node={n} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* The flywheel */}
      <div className="admin-card mt-6">
        <div className="admin-eyebrow mb-3">The loop — each turn makes the next one cheaper</div>
        <div className="flex flex-wrap items-center gap-y-2">
          {MACHINE_LOOP.map((step, i) => (
            <React.Fragment key={step}>
              <span className="rounded-md border border-borderStrong bg-surfaceElevated px-2.5 py-1 text-[12px] text-textPrimary">
                {step}
              </span>
              {i < MACHINE_LOOP.length - 1 ? <span className="px-2 text-mutedDim">→</span> : null}
            </React.Fragment>
          ))}
          <span className="px-2 text-mutedDim">↺</span>
        </div>
      </div>
    </div>
  );
}
